import { useEffect, useMemo, useRef, useState } from "react";

import type { SessionAuditLog } from "@don/wire";

import { googleConfig } from "./auth/google.js";
import { SignInScreen } from "./auth/SignInScreen.js";
import {
  createAuthController,
  createOwnerSession,
  type IdentityProvider,
} from "./auth/session.js";
import { API_BASE, BackendError, createBackend, type ApiFailure, type RecordingView } from "./backend/adapter.js";
import { KnowledgeScreen } from "./knowledge/KnowledgeScreen.js";
import { createConversationStore, defaultMemorySource, type ConversationStore, type MemorySource } from "./knowledge/memory.js";
import {
  afterWrite,
  knowledgeSources,
  type KnowledgeSource,
  type SessionKnowledge,
  type SessionKnowledgeSource,
  type SessionKnowledgeView,
} from "./knowledge/source.js";
import { LibraryScreen, type LibraryRead } from "./library/LibraryScreen.js";
import {
  NO_RENEWALS,
  deletedTitle,
  isRenewalRefusal,
  renewalFor,
  withRenewalState,
  withVideoExpiry,
  withoutSession,
  type RenewalStates,
  type RetentionSource,
} from "./library/retention.js";
import type { LibraryRow } from "./library/row.js";
import type { SearchSource } from "./library/search.js";
import {
  SESSIONS,
  SETTINGS,
  atMsOf,
  navigate,
  recordingPath,
  redirect,
  rowPath,
  segmentMomentPath,
  useRoute,
} from "./routing/route.js";
import type { KnowledgeWrites } from "./session/ReadingColumn.js";
import { SessionScreen, type SessionRead } from "./session/SessionScreen.js";
import type { ReadingPort } from "./session/reading.js";
import type { SessionMedia } from "./session/timeline.js";
import type { TeamSource } from "./settings/team.js";
import { TeamScreen } from "./settings/TeamScreen.js";
import { ConsoleShell } from "./shell/ConsoleShell.js";
import { NotFound } from "./shell/NotFound.js";

/**
 * What a test drives the Console with. **Development builds only.**
 *
 * The library and the open session come from the backend (`backend/adapter.ts`, issue 90);
 * a test hands the app the rows, media and reading the case needs instead. **A handed port
 * wins**: once a test has applied the library, or a session's media or reading, a read that
 * settles later is dropped rather than written over what the test put there. `App` owns the
 * state and nothing below it fetches, so no screen knows which of the two filled it.
 *
 * **It is gated to `import.meta.env.DEV`, and the earlier `window.__firstRun` precedent
 * does not license leaving it ungated.** That one is a production IPC seam with a named
 * non-test driver — the recorder process, over `file://`, by its own comment. This one has
 * no driver but Playwright and is served over HTTP to a browser, where a global that
 * rewrites the owner's library and moves every deletion countdown is a thing any script
 * reaching the page can call.
 */
export interface ConsoleHandle {
  applyLibrary(rows: LibraryRow[]): void;
  /** Fix the clock, so a countdown's three states are reachable from a test. */
  applyNow(nowMs: number): void;
  /**
   * Where the search box's corpus answers come from, or `null` for a Console with no corpus
   * to ask (issue 26).
   *
   * **It joins this handle rather than becoming a second global.** `window.__search` beside
   * `window.__console` would be a second thing to gate, a second thing to remember, and —
   * measured on issue 25 — a second thing `tests/bundle.test.ts` would not catch, since that
   * test pins the spellings it was written against. One handle, one `import.meta.env.DEV`
   * guard, one entry in the list.
   */
  applySearchSource(source: SearchSource | null): void;
  /**
   * What the session detail plays: tracks, epochs and attributed gaps (issue 28), or
   * `null` for a Console that has read no media.
   *
   * On this handle for the reason every other `apply*` is (issue 26's Decision 11): one
   * global means one `import.meta.env.DEV` guard and one entry on `tests/bundle.test.ts`'s
   * list, and that test is a list of spellings rather than a gate on the class, so a
   * `window.__player` beside this one would ship ungated with the suite green.
   */
  applySessionMedia(media: SessionMedia | null): void;
  /**
   * What the session detail's reading column shows: the rail, the transcript, the notes and
   * the two artifact states (issue 29), or `null` for a Console that has read nothing, or
   * `"failed"` for a reading read that did not answer while the video did (issue 202, catalog
   * `session-reading-failed`).
   *
   * On this handle for the reason every other `apply*` is (issue 26's Decision 11), and the
   * reason is sharper here than anywhere: `tests/bundle.test.ts` is a list of spellings, so
   * a `window.__reading` beside this one would ship ungated with the suite green, and what
   * it would carry is a person's transcribed speech.
   */
  applySessionReading(reading: ReadingPort): void;
  /**
   * Where the Team table's rows come from (issue 212), or `null` for the backend's own
   * `GET /people`, which every build reads otherwise. A handed source wins, so a test can hold
   * the read in flight, fail it, or answer it.
   *
   * On this handle for the reason every other `apply*` is (issue 26's Decision 11): one
   * global means one `import.meta.env.DEV` guard and one entry on `tests/bundle.test.ts`'s
   * list, and that test is a list of spellings rather than a gate on the class, so a
   * `window.__people` beside this one would ship ungated with the suite green.
   */
  applyPeople(source: TeamSource | null): void;
  /**
   * The audit log (issue 31), or `null` for a Console that has read nothing. Nothing renders
   * it since issue 212 removed the Activity page (decision 2); the handle stays until issue
   * 224 settles what of it outlives the screen.
   */
  applyAudit(log: SessionAuditLog | null): void;
  /**
   * Where the owner's renewal and delete go (issue 30), or `null` for a Console with no
   * server to send them to.
   *
   * On this handle for the reason every other `apply*` is (issue 26's Decision 11), and the
   * consequence of a second global would be worse here than anywhere it has been so far:
   * `tests/bundle.test.ts` is a list of spellings, so a `window.__retention` beside this one
   * would ship ungated, and what it would carry is a way to destroy somebody's recording.
   *
   * `null` is not a Console whose renewals fail. With no source the controls are not
   * offered at all, because a Renew that did nothing would be a button promising the one
   * thing standing between a recording and deletion.
   */
  applyRetention(source: RetentionSource | null): void;
  /**
   * A test identity for the sign-in screen (issue 118), or `null` for the build's own Google
   * button (issue 119). The stack spec hands in one that reads a token from a fake identity
   * endpoint; set, it replaces the Google button.
   *
   * On this handle for the reason every other `apply*` is (issue 26's Decision 11).
   */
  applySignInProvider(provider: IdentityProvider | null): void;
  /**
   * A knowledge source for the session's side column (issue 202, decision 38), or `null` for
   * none: the column then reads `Coming soon`. Never called in a build: there the side column
   * reads the derived route (issue 229, `knowledgeSources`). Tests and the visual gate hand one
   * in. It feeds the session column only: the org half went with the hub (issue 286).
   *
   * On this handle for the reason every other `apply*` is (issue 26's Decision 11).
   */
  applyKnowledge(source: KnowledgeSource | null): void;
  /**
   * Where the Knowledge page reads a person's memory and asks over it (issue 276, design-spec
   * §9.1), or `null` for the default source. A handed source wins. The default is
   * `defaultMemorySource(backend)`: topics, facts and ask from the backend.
   *
   * On this handle for the reason every other `apply*` is (issue 26's Decision 11).
   */
  applyMemory(source: MemorySource | null): void;
  /**
   * Draw the empty library's `Invite someone` (issue 197), which the deployed Console hides
   * (issue 118, story 19). Settings is no longer behind it: issue 212 put Settings · Team in
   * production. Production has no way to reach this. Search and the retention controls need no
   * switch: each is drawn only once a source is applied, and none is in production.
   */
  revealHiddenScreens(): void;
}

declare global {
  interface Window {
    __console?: ConsoleHandle;
  }
}

/**
 * The owner's session: the exchange and sign-out calls (issue 118). The cookie itself is the
 * browser's, `HttpOnly`, and this app never sees it.
 */
const ownerSession = createOwnerSession();

/**
 * Google sign-in (issue 119): the pinned OAuth client, the browser key the build was given,
 * and this page's origin. `null` in a production build made without a key.
 */
const google = googleConfig(import.meta.env, window.location.origin);

export function App({ onApiFailure }: { onApiFailure?: (code: ApiFailure) => void } = {}) {
  const route = useRoute();
  // A test identity that replaces the Google button (dev only); `null` is Google's.
  const [provider, setProvider] = useState<IdentityProvider | null>(null);
  // The empty library's Invite, drawn only on a dev-only reveal (story 19).
  const [hiddenShown, setHiddenShown] = useState(false);
  // The last sign-out's `POST /auth/signout` failed: the page is cleared, but the cookie may
  // still be valid until its 12-hour mark, and the sign-in screen says so (story 14).
  const [signOutUnconfirmed, setSignOutUnconfirmed] = useState(false);
  // Read once at mount rather than on every render: a countdown that re-derived itself
  // mid-render would make two cells in one row disagree about what "now" is.
  const [nowMs, setNowMs] = useState(() => Date.now());
  // **The library is what `GET /recordings` answered (issue 321), and empty until it has.** Where the read
  // is (`libraryRead`) is held beside it, because an empty list alone cannot say "nothing
  // recorded", "not answered yet" and "the read failed" apart (issues 90, 197). The design
  // bundle's sample rows are gone: the dev stack (issue 78) has real sessions to show.
  const [rows, setRows] = useState<LibraryRow[]>([]);
  const [libraryRead, setLibraryRead] = useState<LibraryRead>("reading");
  // What the failed read's `Try again` moves: the library effect re-reads on it.
  const [libraryAttempt, setLibraryAttempt] = useState(0);
  // Which ports a test has handed over, so a read that settles afterwards does not
  // overwrite them. A ref, because it is a fact about the page rather than something drawn.
  const handed = useRef({ library: false, session: false });
  // The same fact for the session, as state, because the knowledge sources are drawn from it: a
  // session a test handed in has no derived route to ask (issue 229).
  const [sessionHanded, setSessionHanded] = useState(false);

  // **No corpus source by default, and that is the same honesty the empty library is.** The
  // adapter reads the library and one session (issue 90) and nothing else, so there is
  // nothing to ask what a transcript says, and with no source the Sessions screen draws no
  // search field at all rather than promising what it cannot reach (issue 194).
  // The day `POST /sessions/search` is reachable from a browser, a `fetch` adapter is what
  // goes here and no screen below changes. It must reject rather than resolve empty when the
  // request fails: a failure is a state the screen renders, not an answer.
  const [searchSource, setSearchSource] = useState<SearchSource | null>(null);

  // **The open session's media and reading, `null` until read**, and `null` is neither an
  // empty timeline nor a session nobody spoke during: it is a Console that has not heard
  // back. Both come from one read (`Backend.session`): the playlists and the derived route
  // (issue 228). The reading carries the two artifact states, the Summary's paragraph and
  // steps, and the Transcript's bookend lines; no route answers notes (`sessionReadingOf`).
  const [media, setMedia] = useState<SessionMedia | null>(null);
  const [reading, setReading] = useState<ReadingPort>(null);
  // Where that read is while `media` is `null`, so the screen can say "reading" and "could
  // not read" apart (issue 135). `attempt` is what a retry moves: the effect re-reads on it.
  const [sessionRead, setSessionRead] = useState<SessionRead>("reading");
  const [attempt, setAttempt] = useState(0);

  // **The Team table reads `GET /people` in every build** (issue 212); a source a test handed
  // over replaces that read. No audit log by default: `GET /audit` is not called, and nothing
  // renders one since the Activity page went (issue 212).
  const [handedPeople, setHandedPeople] = useState<TeamSource | null>(null);
  const [, setAudit] = useState<SessionAuditLog | null>(null);

  // **No retention source by default, for the reason every other port defaults to `null`.**
  // `POST /sessions/{id}/renew` and `POST /sessions/{id}/delete` exist (issue 30) and the
  // Console does not call them: the adapter reads the library and one session (issue 90,
  // `backend/adapter.ts`) and nothing else. With none, no renewal control and no delete
  // control are rendered anywhere, which is the honest state: an offer to renew that reaches
  // nothing is a promise about a deletion date that this page cannot keep.
  const [retention, setRetention] = useState<RetentionSource | null>(null);
  // **The session's knowledge source (issue 229).** Nothing handed in (`undefined`), the side
  // column reads the derived route (`knowledgeSources`). A source handed in through
  // `applyKnowledge`, `null` included, answers it instead. `sessionKnowledge` is the open
  // session's answer, `null` until one arrives.
  // The memory source a test handed in (issue 276); `null` is the default source.
  const [handedMemory, setHandedMemory] = useState<MemorySource | null>(null);
  const [appliedKnowledge, setAppliedKnowledge] = useState<KnowledgeSource | null | undefined>(undefined);
  // **Keyed by session, as `renewals` is below** (issue 204, 202 review P1-2): one value made
  // the first render of session B draw session A's answer until the effect cleared it.
  const [sessionKnowledge, setSessionKnowledge] = useState<{
    readonly sessionId: string;
    readonly answer: SessionKnowledge | "failed";
  } | null>(null);
  const [knowledgeAttempt, setKnowledgeAttempt] = useState(0);
  // **Keyed by session, because a renewal is a fact about one recording.** One value here
  // made a refusal about the recording the owner had just left into a sentence under the
  // chip of the one they opened next, and made `Renew all N`'s concurrent settles overwrite
  // each other until only the last one survived.
  const [renewals, setRenewals] = useState<RenewalStates>(NO_RENEWALS);
  // What the last owner delete removed, so a deletion that happened is not silent.
  const [deleted, setDeleted] = useState<string | null>(null);

  // **The Ask conversations live here, not in the Knowledge screen** (issue 282): an answer keeps
  // arriving while the owner is on another person or tab (`createConversationStore`). One store
  // for the page's life; a reload starts empty, and sign-out empties it (`clearPage`, issue 283).
  const conversations = useRef<ConversationStore | null>(null);
  conversations.current ??= createConversationStore();

  // **Sign-out and an expired session end the same way**: everything an owner read put on
  // the page is dropped (Ask conversations included, their streams aborted), and the page is
  // sign-in. `clear` is a ref so the controller built once always reaches the current setters.
  const clearPage = useRef(() => undefined as void);
  clearPage.current = () => {
    conversations.current?.clear();
    setRows([]);
    setLibraryRead("reading");
    setMedia(null);
    setReading(null);
    setSessionRead("reading");
    setRenewals(NO_RENEWALS);
    setDeleted(null);
  };
  const auth = useMemo(
    () => createAuthController(ownerSession, { clear: () => clearPage.current(), redirect }),
    [],
  );
  // The one data source. Same-origin `/api`, which the dev server proxies (`dev-origins.ts`);
  // a 401 from any adapter read returns to sign-in (`backend/adapter.ts`), and a 5xx or an
  // unreachable backend goes to the error reporter (issue 243, `main.tsx`).
  const backend = useMemo(
    () => createBackend(fetch, API_BASE, auth.expired, onApiFailure),
    [auth, onApiFailure],
  );
  const signingIn = route.kind === "sign-in";
  const teamSource = useMemo<TeamSource>(
    () =>
      handedPeople ?? {
        read: () => backend.people(),
        issueCode: (personId) => backend.issuePairingCode(personId),
        add: (request) => backend.addTeammate(request),
        edit: (personId, request) => backend.editTeammate(personId, request),
        deactivate: (personId) => backend.deactivateTeammate(personId),
        reactivate: (personId) => backend.reactivateTeammate(personId),
      },
    [handedPeople, backend],
  );

  // What the Knowledge page reads about a person's memory (issue 276); the page reads its
  // roster from `teamSource`, the Team table's own read (issue 277).
  // Topics, facts and ask come from the backend (`defaultMemorySource`).
  const memorySource = useMemo<MemorySource>(
    () => handedMemory ?? defaultMemorySource(backend),
    [handedMemory, backend],
  );

  useEffect(() => {
    // The sign-in page reads nothing: there is nobody to read as yet. Arriving back on the
    // library after a sign-in runs this again, which is the read that proves the cookie.
    if (signingIn) return;
    let live = true;
    if (!handed.current.library) setLibraryRead("reading");
    backend.library().then(
      (read) => {
        if (!live || handed.current.library) return;
        setRows(read);
        setLibraryRead("read");
      },
      () => {
        if (!live || handed.current.library) return;
        setLibraryRead("failed");
      },
    );
    return () => {
      live = false;
    };
  }, [backend, signingIn, libraryAttempt]);

  // The Knowledge page's own library reads (issue 285): the rows a test handed in, or
  // `GET /recordings`. A ref, so a read sent later sees rows handed after this render.
  const rowsNow = useRef(rows);
  rowsNow.current = rows;
  const knowledgeLibrary = () => (handed.current.library ? Promise.resolve(rowsNow.current) : backend.library());
  // Its clock for those stamps: wall milliseconds, made strictly increasing so a read sent after
  // an item was shown is later than it even within one millisecond (`linkOf`).
  const lastStamp = useRef(0);
  const knowledgeNow = () => (lastStamp.current = Math.max(Date.now(), lastStamp.current + 1));

  const openSessionId = route.kind === "session" ? route.sessionId : null;
  // A recording's detail (issue 321): the same screen, read in one `GET /recordings/{id}`, which
  // answers its knowledge too, so no session knowledge source is asked for it.
  const openRecording = route.kind === "session" && route.recording === true;
  const [recordingKnowledge, setRecordingKnowledge] = useState<{
    readonly sessionId: string;
    readonly answer: SessionKnowledge | "failed";
  } | null>(null);
  // **A link naming a recording's segment opens the recording at that moment** (issue 322, PRD
  // stories 26 and 27): `/sessions/{segment}?at=t`, which Knowledge, Ask and every other moment
  // link spell (`momentPath`), is replaced by `/recordings/{id}?at=offset+t` once the library
  // says which recording holds the segment and the recording's read says where it starts
  // (`segmentMomentPath`). The read is kept for the recording route, so it is not asked twice.
  const segmentOf =
    route.kind === "session" && route.recording !== true
      ? rows.find((row) => row.recordingId !== undefined && row.segments?.some((s) => s.sessionId === route.sessionId))
          ?.recordingId
      : undefined;
  const resolved = useRef<{ readonly recordingId: string; readonly view: RecordingView } | null>(null);
  useEffect(() => {
    if (segmentOf === undefined || openSessionId === null || handed.current.session) return;
    let live = true;
    const at = atMsOf(window.location.search);
    backend.recording(segmentOf).then(
      (view) => {
        if (!live) return;
        resolved.current = { recordingId: segmentOf, view };
        redirect(segmentMomentPath(segmentOf, view.reading.segments ?? [], openSessionId, at));
      },
      (error: unknown) => {
        if (!live || (error instanceof BackendError && error.status === 401)) return;
        // The recording's own route draws the failure and its retry.
        redirect(recordingPath(segmentOf));
      },
    );
    return () => {
      live = false;
    };
  }, [backend, segmentOf, openSessionId]);
  useEffect(() => {
    if (openSessionId === null || handed.current.session || segmentOf !== undefined) return;
    let live = true;
    setMedia(null);
    setReading(null);
    setSessionRead("reading");
    // A failed read leaves both `null` and says it failed, so the screen draws a failure
    // and a retry rather than the in-flight state (issue 135). A 401 is not drawn: the
    // adapter has already sent the page to sign-in (`createBackend`'s `onUnauthorized`).
    const kept = resolved.current;
    resolved.current = null;
    const read = openRecording
      ? (kept !== null && kept.recordingId === openSessionId ? Promise.resolve(kept.view) : backend.recording(openSessionId)).then((view) => {
          if (live) setRecordingKnowledge({ sessionId: openSessionId, answer: view.knowledge });
          return view;
        })
      : backend.session(openSessionId);
    read.then(
      (read) => {
        if (!live || handed.current.session) return;
        setMedia(read.media);
        setReading(read.reading);
      },
      (error: unknown) => {
        if (!live || handed.current.session) return;
        if (error instanceof BackendError && error.status === 401) return;
        if (openRecording) setRecordingKnowledge({ sessionId: openSessionId, answer: "failed" });
        setSessionRead("failed");
      },
    );
    return () => {
      live = false;
    };
  }, [backend, openSessionId, openRecording, segmentOf, attempt]);

  // The derived route's session read (issue 229), and which source each place reads.
  const routeKnowledge = useMemo(
    (): SessionKnowledgeSource => ({ session: (sessionId) => backend.knowledge(sessionId) }),
    [backend],
  );
  const { session: sessionSource, workflows: sourceAnswersWorkflows } = useMemo(
    () => knowledgeSources(appliedKnowledge, sessionHanded ? null : routeKnowledge),
    [appliedKnowledge, sessionHanded, routeKnowledge],
  );

  // The open session's knowledge, asked of the source whenever either changes or either
  // `Try again` is pressed: the reading's too (`attempt`), because under the route source the
  // reading and the knowledge are one `/derived` read, so a reading retry that succeeds must
  // not leave the knowledge on the failure it replaced (issue 229 review P0-1). Both effects
  // fire in one commit and share the one in-flight read (`backend/adapter.ts`). A rejection is a failed read, drawn as one (issue 204, 202 review P1-3): a
  // failure is not an answer, and a section left blank would hide it.
  useEffect(() => {
    setSessionKnowledge(null);
    if (sessionSource === null || openSessionId === null || openRecording) return;
    let live = true;
    sessionSource.session(openSessionId).then(
      (answer) => {
        if (live) setSessionKnowledge({ sessionId: openSessionId, answer });
      },
      () => {
        if (live) setSessionKnowledge({ sessionId: openSessionId, answer: "failed" });
      },
    );
    return () => {
      live = false;
    };
  }, [sessionSource, openSessionId, openRecording, knowledgeAttempt, attempt]);

  // What the side column draws: the answer only when it is this session's. A recording's comes
  // from its own read (issue 321).
  const knowledgeView: SessionKnowledgeView = openRecording
    ? recordingKnowledge === null || recordingKnowledge.sessionId !== openSessionId
      ? "unanswered"
      : recordingKnowledge.answer
    : sessionSource === null
      ? "none"
      : sessionKnowledge === null || sessionKnowledge.sessionId !== openSessionId
        ? "unanswered"
        : sessionKnowledge.answer;

  // **Keep and Discard change the item when the source says they happened, and not before**
  // (the rule `deleteSession` states). With a source that offers no write, there are none, and
  // no item draws the buttons. A rejection reaches `KnowledgeItems`, which says so.
  const knowledgeWrites = useMemo((): KnowledgeWrites | undefined => {
    const keep = sessionSource?.keep;
    const discard = sessionSource?.discard;
    if (sessionSource === null || keep === undefined || discard === undefined || openSessionId === null || openRecording) {
      return undefined;
    }
    const write = (kind: "keep" | "discard", send: typeof keep) => async (itemId: string) => {
      await send.call(sessionSource, openSessionId, itemId);
      setSessionKnowledge((current) =>
        current === null || current.sessionId !== openSessionId || current.answer === "failed"
          ? current
          : { sessionId: current.sessionId, answer: afterWrite(current.answer, itemId, kind) },
      );
    };
    return { keep: write("keep", keep), discard: write("discard", discard) };
  }, [sessionSource, openSessionId, openRecording]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.__console = {
      applyLibrary: (next) => {
        handed.current.library = true;
        setLibraryRead("read");
        setRows(next);
      },
      applyNow: (next) => setNowMs(next),
      // Wrapped in a thunk: `setState` calls a function argument to compute the next state,
      // so a source passed bare would be invoked with the previous one instead of stored.
      applySearchSource: (next) => setSearchSource(() => next),
      applySessionMedia: (next) => {
        handed.current.session = true;
        setSessionHanded(true);
        setMedia(next);
      },
      applySessionReading: (next) => {
        handed.current.session = true;
        setSessionHanded(true);
        setReading(next);
      },
      // Wrapped in a thunk, as `applySearchSource` is.
      applyPeople: (next) => setHandedPeople(() => next),
      applyAudit: (next) => setAudit(next),
      // Wrapped in a thunk, as `applySearchSource` is: `setState` calls a function argument
      // to compute the next state, so a source passed bare would be invoked with the
      // previous one instead of stored.
      applyRetention: (next) => setRetention(() => next),
      applySignInProvider: (next) => setProvider(next),
      // Wrapped in a thunk, as `applySearchSource` is.
      applyKnowledge: (next) => setAppliedKnowledge(() => next),
      // Wrapped in a thunk, as `applySearchSource` is.
      applyMemory: (next) => setHandedMemory(() => next),
      revealHiddenScreens: () => setHiddenShown(true),
    };
    return () => {
      delete window.__console;
    };
  }, []);

  // **One renewal, two call sites.** The library's red-marker Renew, its
  // `Renew all (n)` and the session detail's control are all this function: the rows live
  // here, so the screen that moved a date is not the screen that holds it, and "the inline
  // Renew invokes the same path as the detail control" is what the code is rather than
  // something two implementations agree on today.
  //
  // The new date is the server's answer, never a date computed here (`retention.ts`).
  // **Which call a row's key goes to** (issue 322, 321 code-quality P1-1): a recording row's key
  // is its recording id, which goes to the recording's own call and never to a session route;
  // the open recording counts as one before the library has answered.
  const recordingKey = (key: string): string | undefined =>
    rowsNow.current.find((row) => row.sessionId === key)?.recordingId ??
    (route.kind === "session" && route.recording === true && route.sessionId === key ? key : undefined);
  const recordingKeyNow = useRef(recordingKey);
  recordingKeyNow.current = recordingKey;

  const renewSession = useMemo(() => {
    if (retention === null) return undefined;
    return async (sessionId: string) => {
      // Every write below merges into the map rather than replacing it, which is what lets
      // `Renew all N` fire N of these at once and end holding every outcome: a refusal that
      // settles first is still there when the last success settles.
      setRenewals((current) => withRenewalState(current, sessionId, { kind: "working" }));
      try {
        const recordingId = recordingKeyNow.current(sessionId);
        const expiresMs = (
          recordingId === undefined ? await retention.renew(sessionId) : await retention.renewRecording(recordingId)
        ).video_expires_at_ms;
        setRows((current) => withVideoExpiry(current, sessionId, expiresMs));
        setRenewals((current) => withRenewalState(current, sessionId, { kind: "idle" }));
      } catch (error) {
        // Refused and unreachable are two different facts about the recording, and only the
        // adapter knows which: a term that has already ended is final, and a request that
        // never arrived is not. Either way the rows are untouched, so the chip has not moved
        // and the screen is not claiming a renewal.
        const kind = isRenewalRefusal(error) ? "refused" : "failed";
        setRenewals((current) => withRenewalState(current, sessionId, { kind }));
      }
    };
  }, [retention]);

  // **The row goes when the server says it went, and not before.** Nothing is caught here:
  // a rejection reaches the confirmation that asked for it, which is the only place that can
  // say so to the owner. Removing the row first and putting it back on a failure would be
  // the screen claiming a deletion it has not performed, which is the rule decisions §(b)
  // makes about the employee's control and this is the owner's half of.
  const deleteSession = useMemo(() => {
    if (retention === null) return undefined;
    return async (sessionId: string) => {
      // **Read before the await**, because the row is about to stop existing and the
      // notice below has to name the recording that went.
      const row = rows.find((each) => each.sessionId === sessionId);
      const recordingId = recordingKeyNow.current(sessionId);
      setDeleted(null);
      await (recordingId === undefined ? retention.destroy(sessionId) : retention.destroyRecording(recordingId));
      // **The deletion that did happen says so too.** The row is removed and the owner is
      // routed to a list one row shorter, which on a library of near-identical rows is not
      // a difference anybody can see, and on a scrolled one may be off screen entirely. A
      // screen-reader user got a route change and nothing else. Decisions §(b) forbids
      // claiming a deletion that did not happen; it does not license silence about one
      // that did.
      setDeleted(row === undefined ? null : deletedTitle(row));
      // Decisions §(a): a fully deleted session does not render at all, so the row goes and
      // the owner is put back on the list rather than left on a detail page with no subject.
      setRows((current) => withoutSession(current, sessionId));
      navigate(SESSIONS);
    };
  }, [retention, rows]);

  const openRow = useMemo(() => {
    if (route.kind !== "session") return undefined;
    const listed = rows.find((row) => row.sessionId === route.sessionId);
    if (listed !== undefined || route.recording === true) return listed;
    // A session that is a recording's segment has no row of its own since issue 321; a link
    // naming it opens the recording (`segmentOf`), and until then it is drawn under its own row.
    return rows.flatMap((row) => row.segments ?? []).find((row) => row.sessionId === route.sessionId);
  }, [route, rows]);
  // A segment on its own offers no renewal and no delete (321 security P1-1): both act on the
  // whole recording, which is where the link is taking the owner.
  const openSegment = segmentOf !== undefined;

  return (
    <ConsoleShell
      route={route}
      onSignOut={() => {
        setSignOutUnconfirmed(false);
        void auth.signOut().catch(() => setSignOutUnconfirmed(true));
      }}
    >
      {route.kind === "sign-in" ? (
        <SignInScreen
          provider={provider}
          google={google}
          signOutUnconfirmed={signOutUnconfirmed}
          onSignIn={async (chosen) => {
            await auth.signIn(chosen);
            setSignOutUnconfirmed(false);
          }}
        />
      ) : route.kind === "sessions" ? (
        <LibraryScreen
          rows={rows}
          nowMs={nowMs}
          searchSource={searchSource}
          read={libraryRead}
          onRetryRead={() => setLibraryAttempt((count) => count + 1)}
          onInvite={hiddenShown ? () => navigate(SETTINGS) : undefined}
          onOpen={(row) => navigate(rowPath(row))}
          onRenew={renewSession}
          renewals={renewals}
          deleted={deleted}
          onDismissDeleted={() => setDeleted(null)}
        />
      ) : route.kind === "session" ? (
        <SessionScreen
          sessionId={route.sessionId}
          row={openRow}
          media={media}
          reading={reading}
          nowMs={nowMs}
          onRenew={openSegment ? undefined : renewSession}
          onDelete={openSegment ? undefined : deleteSession}
          renewal={renewalFor(renewals, route.sessionId)}
          read={sessionRead}
          onRetry={() => setAttempt((count) => count + 1)}
          knowledge={knowledgeView}
          workflows={sourceAnswersWorkflows && !openRecording ? knowledgeView : "none"}
          knowledgeWrites={knowledgeWrites}
          onRetryKnowledge={() =>
            openRecording ? setAttempt((count) => count + 1) : setKnowledgeAttempt((count) => count + 1)
          }
          // A moment's link opens the session there (issue 280): `?at=`, all digits or ignored.
          initialAtMs={atMsOf(window.location.search)}
        />
      ) : route.kind === "knowledge" ? (
        <KnowledgeScreen
          team={teamSource}
          memory={memorySource}
          conversations={conversations.current}
          personId={route.personId}
          nowMs={nowMs}
          library={knowledgeLibrary}
          now={knowledgeNow}
        />
      ) : route.kind === "settings" ? (
        <TeamScreen source={teamSource} nowMs={nowMs} />
      ) : route.kind === "not-found" ? (
        // Any other path: Not found (issue 199).
        <NotFound path={route.path} />
      ) : // `/connect` is never mounted here: `main.tsx` mounts `ConnectApp` instead (issue 392).
      null}
    </ConsoleShell>
  );
}
